import { BookOpen } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { NavLink, useParams } from "react-router-dom";

import { Markdown } from "@/client/components/markdown";
import { PageHeader } from "@/client/components/page-header";
import { Card, CardContent } from "@/client/components/ui/card";
import { Skeleton } from "@/client/components/ui/skeleton";
import { type DocNavPage, docsApi, type DocPageContent } from "@/client/features/docs/docs-api";
import { ApiError } from "@/client/lib/api";
import { cn } from "@/client/lib/utils";
import { compareDocTitleOrder, displayDocTitle } from "@/shared/doc-title";

interface TreeNode extends DocNavPage {
  children: TreeNode[];
}

function buildTree(pages: DocNavPage[]): TreeNode[] {
  const byDocId = new Map<string, TreeNode>();
  for (const p of pages) {
    byDocId.set(p.outlineDocumentId, { ...p, children: [] });
  }
  const roots: TreeNode[] = [];
  for (const node of byDocId.values()) {
    const parent = node.parentOutlineDocumentId
      ? byDocId.get(node.parentOutlineDocumentId)
      : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRec = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => compareDocTitleOrder(a.title, b.title) || a.position - b.position);
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

function NavTree({ nodes, depth = 0 }: { nodes: TreeNode[]; depth?: number }) {
  return (
    <ul className="space-y-0.5">
      {nodes.map((node) => (
        <li key={node.id}>
          <NavLink
            to={`/docs/${node.slug}`}
            style={{ paddingLeft: `${depth * 12 + 8}px` }}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2 rounded-md py-1.5 pr-2 text-sm",
                isActive
                  ? "bg-accent text-accent-foreground font-medium"
                  : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
              )
            }
          >
            <span className="truncate">{displayDocTitle(node.title)}</span>
          </NavLink>
          {node.children.length > 0 ? <NavTree nodes={node.children} depth={depth + 1} /> : null}
        </li>
      ))}
    </ul>
  );
}

function DocViewer({ slug }: { slug: string }) {
  const [page, setPage] = useState<DocPageContent | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  useEffect(() => {
    let active = true;
    setState("loading");
    setPage(null);
    (async () => {
      try {
        const res = await docsApi.getPage(slug);
        if (!active) return;
        setPage(res);
        setState("ready");
      } catch (err) {
        if (!active) return;
        setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
      }
    })();
    return () => {
      active = false;
    };
  }, [slug]);

  if (state === "loading") {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/5" />
      </div>
    );
  }
  if (state === "notfound") {
    return <p className="text-muted-foreground text-sm">This document isn't available.</p>;
  }
  if (state === "error" || !page) {
    return <p className="text-muted-foreground text-sm">Couldn't load this document. Try again.</p>;
  }

  return (
    <article className="min-w-0">
      <h1 className="mb-1 text-2xl font-semibold tracking-tight">{displayDocTitle(page.title)}</h1>
      {page.outlineUpdatedAt ? (
        <p className="text-muted-foreground mb-6 text-xs">
          Updated {new Date(page.outlineUpdatedAt).toLocaleDateString()}
        </p>
      ) : null}
      <Markdown content={page.content} />
    </article>
  );
}

function SidebarNav({
  error,
  pages,
  tree,
}: {
  error: boolean;
  pages: DocNavPage[] | null;
  tree: TreeNode[];
}) {
  if (error) {
    return (
      <p className="text-muted-foreground text-sm">
        Couldn't load the document list. Refresh to try again.
      </p>
    );
  }
  if (pages === null) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }
  if (pages.length === 0) {
    return <p className="text-muted-foreground text-sm">No documentation published yet.</p>;
  }
  return <NavTree nodes={tree} />;
}

export function DocsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [pages, setPages] = useState<DocNavPage[] | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await docsApi.listPages();
        if (active) {
          setPages(res.pages);
          setError(false);
        }
      } catch {
        if (active) setError(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const tree = useMemo(() => (pages ? buildTree(pages) : []), [pages]);

  // With nothing selected and an empty (successfully loaded) library, show the
  // full-width empty state. Otherwise render the two-column layout: the article
  // (DocViewer) loads in parallel and independently of the nav, so a direct
  // /docs/:slug link still works even if the sidebar list fails to load.
  const showEmptyState = !slug && !error && pages?.length === 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Documentation"
        description="Guides and references for your Mantle environment."
      />

      {showEmptyState ? (
        <Card>
          <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-12 text-center text-sm">
            <BookOpen className="size-6" />
            <p>No documentation has been published yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-8 md:grid-cols-[16rem_minmax(0,1fr)]">
          <nav className="md:border-r md:pr-4">
            <SidebarNav error={error} pages={pages} tree={tree} />
          </nav>
          <div className="min-w-0">
            {slug ? (
              <DocViewer slug={slug} />
            ) : (
              <p className="text-muted-foreground text-sm">
                Select a document from the list to start reading.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
